import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { useAppContextController } from "context/AppContext";
import { useOutsideCompanyCache } from "hooks/useCompanyCache";
import { getCompanyImageUrl } from "utils/helpers/upload";
import {
  generateFilledPdfForApplicant,
  saveFormResponses,
  sendFormOTP,
  verifyFormOTP,
} from "api/llm";
import ContainerWrapper from "components/Wrapper/ContainerWrapper";
import MDBox from "components/MDBox";
import { makeStyles } from "@mui/styles";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import EmailIcon from "@mui/icons-material/Email";
import { Check } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import FormRenderer from "../setup/components/DynamicForm/components/FormRenderer";
import { getFormForApplicant } from "./actions/getFormForApplicant";

const useStyles = makeStyles({
  box: {
    marginTop: 20,
    overflow: "visible",
    width: "85%",
  },
});

const StyledImg = styled("img")(() => ({
  borderRadius: "15px",
}));

const RenderForm = () => {
  const classes = useStyles();
  const { company, setCompany, setCompanyType } = useAppContextController();
  const { isLoadingCompany } = useOutsideCompanyCache({
    company,
    setCompany,
    setCompanyType,
    outsideMode: "public",
  });

  const { id: formId, applicantId } = useParams();

  // Form states
  const [form, setForm] = useState(null);
  const [applicant, setApplicant] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [isStarted, setIsStarted] = useState(false);
  const [error, setError] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [completedDate, setCompletedDate] = useState(null);

  // OTP related states
  const [requiresOtp, setRequiresOtp] = useState(true); // Default to true
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpExpired, setOtpExpired] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);

  useEffect(() => {
    const fetchFormData = async () => {
      if (!formId || !applicantId) return;

      try {
        setIsLoading(true);
        // Get form data with applicant info
        const response = await getFormForApplicant(formId, applicantId);

        if (response.success) {
          // The form data is in response.data.form
          // The formData.form contains the actual form fields structure
          setForm(response.data.form);
          setApplicant(response.data.applicant);

          // Set OTP related states from the response
          setRequiresOtp(response.data.requiresOtp || false);
          setOtpVerified(response.data.otpVerified || false);

          // If OTP is already verified, set the form as started
          if (response.data.otpVerified) {
            setIsStarted(true);
          }

          // Check if the form has already been completed
          const shortName = response.data.form.metadata?.shortName;
          if (
            shortName &&
            response.data.applicant?.dynamicForms &&
            response.data.applicant.dynamicForms[shortName] &&
            response.data.applicant.dynamicForms[shortName].submittedDate
          ) {
            setAlreadyCompleted(true);
            setCompletedDate(
              new Date(response.data.applicant.dynamicForms[shortName].submittedDate)
            );

            // Set form values from the saved responses
            setFormValues(response.data.applicant.dynamicForms[shortName] || {});
          }
        } else {
          setError(response.error || "Failed to load form data");
        }
      } catch (err) {
        console.error("Error fetching form data:", err);
        setError(err.message || "An error occurred while fetching the form");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFormData();
  }, [formId, applicantId]);

  const handleSendOTP = async () => {
    if (!formId || !applicantId) return;

    try {
      setIsSendingOtp(true);
      setOtpError(null);

      const response = await sendFormOTP(formId, applicantId);

      if (response.success) {
        setOtpSent(true);
        if (response.data && response.data.expiresAt) {
          setOtpExpiresAt(new Date(response.data.expiresAt));
        } else {
          // Default 15 minutes if not provided
          const expiryTime = new Date();
          expiryTime.setMinutes(expiryTime.getMinutes() + 15);
          setOtpExpiresAt(expiryTime);
        }
      } else {
        setOtpError(response.error || "Failed to send verification code");
      }
    } catch (err) {
      setOtpError(err.message || "An error occurred while sending verification code");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!formId || !applicantId || !otp) return;

    try {
      setIsVerifyingOtp(true);
      setOtpError(null);

      const response = await verifyFormOTP(formId, applicantId, otp);

      if (response.success) {
        setOtpVerified(true);
        setIsStarted(true);
      } else if (response.expired) {
        setOtpExpired(true);
        setOtpError("Verification code has expired. Please request a new one.");
      } else {
        setOtpError(response.error || "Invalid authentication code");
      }
    } catch (err) {
      // Use the exact API error message when available
      if (err.apiError) {
        setOtpError(err.message);
      } else if (err.response?.data?.error) {
        setOtpError(err.response.data.error);
      } else {
        setOtpError(err.message || "An error occurred while verifying code");
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldId]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    if (e) e.preventDefault();

    try {
      setIsSubmitting(true);

      // First save the form responses to the applicant record
      const saveResponse = await saveFormResponses(formId, applicantId, formValues);

      if (!saveResponse.success) {
        throw new Error(saveResponse.error || "Failed to save form responses");
      }

      // Then generate the PDF
      const pdfPayload = {
        formId,
        applicantId,
        formValues,
        outsideMode: "public",
      };

      const pdfResponse = await generateFilledPdfForApplicant(pdfPayload);

      if (pdfResponse.success) {
        setIsComplete(true);
      } else {
        setError(pdfResponse.error || "Failed to submit form");
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setError(err.message || "An error occurred while submitting the form");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render OTP verification screen
  const renderOTPVerification = () => {
    return (
      <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
        <Paper
          elevation={3}
          sx={{
            overflow: "visible",
            position: "relative",
            borderRadius: 4,
            mt: 8,
            mb: 4,
            pt: 8,
            pb: 6,
            px: { xs: 3, sm: 6 },
            background: "linear-gradient(to bottom, #ffffff, #f9fafc)",
          }}
        >
          {/* Avatar at top of card */}
          <Avatar
            sx={{
              position: "absolute",
              top: -36,
              left: "50%",
              transform: "translateX(-50%)",
              width: 72,
              height: 72,
              bgcolor: (theme) => theme.palette.primary.main,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
            }}
          >
            <LockIcon sx={{ fontSize: 36 }} />
          </Avatar>

          <Stack spacing={4} alignItems="center">
            <Stack spacing={2} textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#1a237e" }}>
                Verify Your Identity
              </Typography>
              <Typography
                color="text.secondary"
                sx={{
                  maxWidth: "90%",
                  mx: "auto",
                  fontSize: "1rem",
                  lineHeight: 1.6,
                }}
              >
                For security reasons, we need to verify your identity before you can fill out this
                form.
              </Typography>
            </Stack>

            <Divider sx={{ width: "100%", my: 1 }} />

            <Box sx={{ width: "100%" }}>
              <Stack spacing={4} width="100%">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2.5,
                    width: "100%",
                    p: 2.5,
                    bgcolor: "rgba(33, 150, 243, 0.08)",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "primary.light",
                  }}
                >
                  <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40 }}>
                    <EmailIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Verification code will be sent to:
                    </Typography>
                    <Typography sx={{ fontWeight: 600 }}>
                      {applicant?.email || "Loading..."}
                    </Typography>
                  </Box>
                </Box>

                {otpSent ? (
                  <Stack spacing={3} sx={{ width: "100%" }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="medium"
                      sx={{ textAlign: "center" }}
                    >
                      Enter the 6-digit code sent to your email
                    </Typography>
                    <TextField
                      fullWidth
                      variant="outlined"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      error={!!otpError}
                      helperText={otpError}
                      inputProps={{
                        maxLength: 6,
                        style: {
                          fontSize: "1.75rem",
                          letterSpacing: "0.75em",
                          fontWeight: "bold",
                          textAlign: "center",
                          padding: "16px 8px",
                        },
                      }}
                      sx={{ mb: 1 }}
                    />

                    {otpExpiresAt && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: "center", fontWeight: 500 }}
                      >
                        Code expires at {new Date(otpExpiresAt).toLocaleTimeString()}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleVerifyOTP}
                      disabled={!otp || isVerifyingOtp || otp.length !== 6}
                      sx={{
                        py: 1.5,
                        mt: 1,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        background: "linear-gradient(45deg, #1976d2, #2196f3)",
                        "&:hover": {
                          background: "linear-gradient(45deg, #1565c0, #1976d2)",
                        },
                      }}
                    >
                      {isVerifyingOtp ? (
                        <CircularProgress size={24} sx={{ color: "white" }} />
                      ) : (
                        "Verify & Continue"
                      )}
                    </Button>

                    <Button
                      variant="text"
                      disabled={isSendingOtp}
                      onClick={handleSendOTP}
                      sx={{
                        color: "primary.main",
                        textTransform: "none",
                        fontWeight: 500,
                      }}
                    >
                      {isSendingOtp ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={16} color="inherit" /> Sending...
                        </Box>
                      ) : (
                        "Resend Code"
                      )}
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={3}>
                    <Typography variant="subtitle1" sx={{ textAlign: "center", fontWeight: 500 }}>
                      Click the button below to receive your verification code
                    </Typography>

                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSendOTP}
                      disabled={isSendingOtp}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: "none",
                        fontSize: "1rem",
                        fontWeight: 600,
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        background: "linear-gradient(45deg, #1976d2, #2196f3)",
                        "&:hover": {
                          background: "linear-gradient(45deg, #1565c0, #1976d2)",
                        },
                      }}
                    >
                      {isSendingOtp ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CircularProgress size={20} color="inherit" /> Sending Code...
                        </Box>
                      ) : (
                        "Send Verification Code"
                      )}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Container>
    );
  };

  const renderFormContent = () => {
    // Show loading state first
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      );
    }

    // Error state
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 3 }}>
          {error}
        </Alert>
      );
    }

    // Success state
    if (isComplete) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Alert severity="success" sx={{ my: 3, fontSize: "1.1rem", py: 2 }}>
            Thank you for completing this form. It has been submitted successfully.
          </Alert>
        </Box>
      );
    }

    // Already completed form state
    if (alreadyCompleted) {
      return (
        <Container maxWidth="sm" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={3}
            sx={{
              overflow: "visible",
              position: "relative",
              borderRadius: 4,
              mt: 8,
              mb: 4,
              pt: 6,
              pb: 6,
              px: { xs: 3, sm: 6 },
              background: "linear-gradient(to bottom, #ffffff, #f9fafc)",
              textAlign: "center",
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
              <Avatar
                sx={{
                  bgcolor: "success.main",
                  width: 80,
                  height: 80,
                  boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Check sx={{ fontSize: 40 }} />
              </Avatar>
            </Box>

            <Typography variant="h4" sx={{ fontWeight: 700, color: "#2e7d32", mb: 3 }}>
              Form Already Completed
            </Typography>

            <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
              You have already completed this form on{" "}
              {completedDate?.toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              .
            </Typography>

            <Typography variant="body1" sx={{ mb: 4 }}>
              If you need to modify any information or have questions about your submission, please
              contact our support team for assistance.
            </Typography>

            {/* <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<EmailIcon />}
                href="mailto:support@gignology.com"
                sx={{ mr: 2 }}
              >
                Email Support
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </Box> */}
          </Paper>
        </Container>
      );
    }

    // OTP verification screen
    if (requiresOtp && !otpVerified) {
      return renderOTPVerification();
    }

    // Form rendering
    if (form?.formData?.form) {
      return (
        <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, sm: 3 } }}>
          <Paper
            elevation={3}
            sx={{
              overflow: "visible",
              position: "relative",
              borderRadius: 4,
              mt: 4,
              mb: 4,
              pt: 4,
              pb: 6,
              px: { xs: 3, sm: 6 },
              background: "linear-gradient(to bottom, #ffffff, #f9fafc)",
            }}
          >
            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
                {form.name}
              </Typography>
              {form.metadata?.description && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  {form.metadata.description}
                </Typography>
              )}

              <Typography variant="subtitle1" color="text.secondary" sx={{ fontWeight: 500 }}>
                Applicant: {applicant?.firstName} {applicant?.lastName}
              </Typography>
            </Box>

            <FormRenderer
              formData={form.formData.form}
              formValues={formValues}
              onInputChange={handleInputChange}
              onSubmit={handleFormSubmit}
              applicant={applicant}
            />

            <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleFormSubmit}
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  px: 4,
                  backgroundColor: "#00A3FF",
                  "&:hover": {
                    backgroundColor: "#0091EA",
                  },
                }}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Submit Form"
                )}
              </Button>
            </Box>
          </Paper>
        </Container>
      );
    }

    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        Form data could not be loaded.
      </Alert>
    );
  };

  return (
    <>
      <ContainerWrapper sx={{ px: "20px", pt: "15px" }} position="relative">
        <Box position="relative">
          <a href="/">
            <StyledImg
              src={`${getCompanyImageUrl(company)}/company/${company?.slug}/banner/${
                company?.bannerUrl
              }`}
              alt=""
              width="100%"
            />
          </a>
        </Box>

        {renderFormContent()}

        <MDBox
          sx={{ py: "15px", px: "20px" }}
          bgColor="#222"
          display="flex"
          justifyContent="center"
        >
          <img
            src="/images/png/logos/powered-by-gignology.png"
            alt="powered by gig-nology"
            width="205.8px"
            height="82.8px"
          />
        </MDBox>
      </ContainerWrapper>
    </>
  );
};

export default RenderForm;
